import store from "@/store/VuexPlugin";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

@Module({
  dynamic: true,
  namespaced: true,
  name: "feature",
  store
})
export class FeatureStore extends VuexModule {
  /**
   * Get the current value of the interactive camera setting.
   */
  get isInteractiveCameraEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the Pagination setting.
   */
  get isPaginationEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the PaginationUser setting.
   */
  get isPaginationUserEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the progressStatusEnabled setting.
   */
  get isUpdateProgressStatusEnabled(): boolean {
    return true;
  }

  /**
   * True if settings control is enabled
   */
  get isPartnerManagerEnabled(): boolean {
    return true;
  }

  /**
   * True if partner analytics is enabled.
   */
  get isPartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * True if multipartner analytics is enabled.
   */
  get isMultipartnerAnalyticsEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the isPdfUploadEnabled
   */
  get isPdfUploadEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the template setting.
   */
  get isTemplateEnabled(): boolean {
    return true;
  }

  /**
   * Get the current value of the fleet.
   */
  get isFleetFeatureEnabled(): IFleetFeature {
    const fleet: IFleetFeature = {
      isFleetEnabled: true,
      isDriverEnabled: true,
      isPoolEnabled: true,
      isContractEnabled: true,
      isDocumentEnabled: true
    };

    return fleet;
  }

  /**
   * Get the current value of the signature.
   */
  get isDigitalSignatureEnabled(): IDigitalSignatureFeature {
    const signatureFeature: IDigitalSignatureFeature = {
      isSignEnabled: true
    };
    return signatureFeature;
  }

  /**
   * Get the current value of the report.
   */
  get isReportEnabled(): IReportFeature {
    const report: IReportFeature = {
      isSharingEnabled: true,
      isTimelineEnabled: true
    };

    return report;
  }

  /**
   * Get the current default value of the marketplace.
   */
  get isMarketplaceEnabled(): IMarketplaceFeature {
    const isMarketplaceEnabled: IMarketplaceFeature = {
      isMarketplaceEnabled: true,
      isInsuranceCheckEnabled: true
    };

    return isMarketplaceEnabled;
  }

  /**
   * Setting for reportAdvertismentEnabled.
   */
  private _isReportAdvertisementEnabled =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isReportAdvertisementEnabled || false;

  /**
   * Get the current value of the reportAdvertismentEnabled setting.
   */
  get isReportAdvertisementEnabled(): boolean {
    return this._isReportAdvertisementEnabled;
  }

  /**
   * Dispatches an action to set the report advetisement enabled property.
   *
   * @param value
   */
  @Action
  setReportAdvertisementEnabled(value: boolean) {
    this.context.commit("updateReportAdvertisementEnabled", value);
  }

  /**
   * Mutates the report advetisement enabled property.
   *
   * @param value
   */
  @Mutation
  updateReportAdvertisementEnabled(value: boolean) {
    this._isReportAdvertisementEnabled = value;
  }

  /**
   * Get the current value of the isInviteFriendsEnabled setting.
   */
  get isInviteFriendsEnabled(): boolean {
    return true;
  }

  /**
   * Setting for showing query params in report
   */
  private _isQueryParamsShowing =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isQueryParamsShowing || false;

  /**
   * Get the setting for showing query params in report
   */
  get isQueryParamsShowing(): boolean {
    return this._isQueryParamsShowing;
  }

  /**
   * Dispatches an action to set setting for showing query params in report
   *
   * @param value The value to set the query params to
   */
  @Action
  public setQueryParamsShowing(value: boolean) {
    this.context.commit("_mutateQueryParamsShowing", value);
  }

  /**
   * Mutates the setting for showing query params in report
   *
   * @param value The value to set the query params to
   */
  @Mutation
  private _mutateQueryParamsShowing(value: boolean) {
    this._isQueryParamsShowing = value;
  }

  /**
   * Setting for showing option to export to ksr
   */
  private _isSendToKsrEnabled =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isSendToKsrEnabled || false;

  /**
   * Get the current value of the isSendToKsrEnabled
   */
  get isSendToKsrEnabled(): boolean {
    return this._isSendToKsrEnabled;
  }

  /**
   * Dispatches an action to enable the option to export to ksr
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsSendToKsrEnabled(value: boolean) {
    this.context.commit("_mutateSendToKsrEnabled", value);
  }

  /**
   * Mutates the option to export to ksr
   *
   * @param value The value to set the option to export to ksr.
   */
  @Mutation
  private _mutateSendToKsrEnabled(value: boolean) {
    this._isSendToKsrEnabled = value;
  }

  /**
   * Setting for showing option to export to pdr
   */
  private _isSendToPdrEnabled =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isSendToPdrEnabled || false;

  /**
   * Get the current value of the isSendToPdrEnabled
   */
  get isSendToPdrEnabled(): boolean {
    return this._isSendToPdrEnabled;
  }

  /**
   * Dispatches an action to enable the option to export to pdr
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsSendToPdrEnabled(value: boolean) {
    this.context.commit("_mutateSendToPdrEnabled", value);
  }

  /**
   * Mutates the option to export to pdr
   *
   * @param value The value to set the option to export to pdr.
   */
  @Mutation
  private _mutateSendToPdrEnabled(value: boolean) {
    this._isSendToPdrEnabled = value;
  }

  /**
   * Setting for showing option to export to da
   */
  private _isSendToDaEnabled =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isSendToDaEnabled || false;

  /**
   * Get the current value of the isSendToDaEnabled
   */
  get isSendToDaEnabled(): boolean {
    return this._isSendToDaEnabled;
  }

  /**
   * Dispatches an action to enable the option to export to da
   *
   * @param value The value to set the option to.
   */
  @Action
  public setIsSendToDaEnabled(value: boolean) {
    this.context.commit("_mutateSendToDaEnabled", value);
  }

  /**
   * Mutates the option to enable da upload
   *
   * @param value The value to set the option to export to da.
   */
  @Mutation
  private _mutateSendToDaEnabled(value: boolean) {
    this._isSendToDaEnabled = value;
  }

  /**
   * Setting for partner colors
   */
  private _isVuetifyInPartnerColors =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isVuetifyInPartnerColors || false;
  /**
   * Setting for partner colors
   */
  private _isNavigationInPartnerColors =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isNavigationInPartnerColors || false;
  /**
   * Setting for partner colors
   */
  get isVuetifyInPartnerColors() {
    return this._isVuetifyInPartnerColors;
  }
  /**
   * Setting for partner colors
   */
  get isNavigationInPartnerColors() {
    return this._isNavigationInPartnerColors;
  }

  @Mutation
  private _mutateIsVuetifyInPartnerColors(isVuetifyInPartnerColors: boolean) {
    this._isVuetifyInPartnerColors = isVuetifyInPartnerColors;
  }
  @Mutation
  private _mutateIsNavigationInPartnerColors(isNavigationInPartnerColors: boolean) {
    this._isNavigationInPartnerColors = isNavigationInPartnerColors;
  }

  @Action
  public setIsVuetifyInPartnerColors(isVuetifyInPartnerColors: boolean) {
    this.context.commit("_mutateIsVuetifyInPartnerColors", isVuetifyInPartnerColors);
  }
  @Action
  public setIsNavigationInPartnerColors(isNavigationInPartnerColors: boolean) {
    this.context.commit("_mutateIsNavigationInPartnerColors", isNavigationInPartnerColors);
  }

  /**
   * Setting for online booking
   */
  private _onlineBooking: boolean =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._onlineBooking || false;

  get onlineBooking(): boolean {
    return this._onlineBooking;
  }

  @Mutation
  private _mutateSetOnlineBooking(value: boolean) {
    this._onlineBooking = value;
  }

  @Action
  public setOnlineBooking(value: boolean) {
    this.context.commit("_mutateSetOnlineBooking", value);
  }

  /**
   * Setting on billing page to check if the billing distribution is enabled
   */
  private _isBillingDistributionEnabled: boolean =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isBillingDistributionEnabled || false;
  get isBillingDistributionEnabled(): boolean {
    return this._isBillingDistributionEnabled;
  }
  @Mutation
  private _mutateIsBillingDistributionEnabled(value: boolean) {
    this._isBillingDistributionEnabled = value;
  }
  @Action
  public setIsBillingDistributionEnabled(value: boolean) {
    this.context.commit("_mutateIsBillingDistributionEnabled", value);
  }
  /**
   * Feature Flag for billing overview for partner
   */
  private _isPartnerBillingOverviewEnabled: boolean =
    JSON.parse(window.localStorage.getItem("feature") || "{}").state?._isPartnerBillingOverviewEnabled || false;
  get isPartnerBillingOverviewEnabled(): boolean {
    return this._isPartnerBillingOverviewEnabled;
  }
  @Mutation
  private _mutateIsPartnerBillingOverviewEnabled(value: boolean) {
    this._isPartnerBillingOverviewEnabled = value;
  }
  @Action
  public setIsPartnerBillingOverviewEnabled(value: boolean) {
    this.context.commit("_mutateIsPartnerBillingOverviewEnabled", value);
  }
}

export const FeatureModule = getModule(FeatureStore);

export interface IFleetFeature {
  isFleetEnabled: boolean;
  isDriverEnabled: boolean;
  isPoolEnabled: boolean;
  isContractEnabled: boolean;
  isDocumentEnabled: boolean;
}

export interface IDigitalSignatureFeature {
  isSignEnabled: boolean;
}

export interface IReportFeature {
  isTimelineEnabled: boolean;
  isSharingEnabled: boolean;
}

export interface IMarketplaceFeature {
  isMarketplaceEnabled: boolean;
  isInsuranceCheckEnabled: boolean;
}
